/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { ReactElement, ValidationMap, WeakValidationMap } from "react";

export type DistributiveOmit<T, K extends keyof any> = T extends any ? Omit<T, K> : never;

export type VoidElement =
	| "area"
	| "base"
	| "br"
	| "col"
	| "hr"
	| "img"
	| "input"
	| "link"
	| "meta"
	| "param"
	| "command"
	| "keygen"
	| "source";

export type OmitChildrenFromVoid<C extends React.ElementType> = C extends VoidElement
	? Omit<React.ComponentPropsWithRef<C>, "children">
	: React.ComponentPropsWithRef<C>;

/**
 * A component whose root component can be controlled via a `Component` prop.
 */
export interface OverridableComponent<P, D extends React.ElementType = "div"> {
	<C extends React.ElementType>(
		props: { Component: C } & P & DistributiveOmit<OmitChildrenFromVoid<C>, keyof P>,
		context?: unknown
	): JSX.Element;

	(props: P & DistributiveOmit<React.ComponentPropsWithRef<D>, keyof P>, context?: unknown): JSX.Element;

	propTypes?: WeakValidationMap<P> | undefined;
	contextTypes?: ValidationMap<unknown> | undefined;
	defaultProps?: Partial<P> | Partial<P & { Component: React.ElementType }> | undefined;
	displayName?: string | undefined;
}

export interface ICommonProps {
	children?: React.ReactNode;
	className?: string;
}

export type OverrideComponentProps<P, E extends React.ElementType, C extends React.ElementType = E> = {
	Component: C;
} & P &
	Omit<React.ComponentPropsWithRef<C>, keyof P>;

interface OverridenForwardRefRenderFunction<P, D extends React.ElementType> {
	<C extends React.ElementType>(
		props: P & { Component: C } & React.ComponentPropsWithRef<C>,
		ref: React.ForwardedRef<React.ComponentType<C>>
	): ReactElement | null;

	displayName?: string | undefined;
	defaultProps?: never | undefined;
	propTypes?: never | undefined;
}

export function overrideForwardRef<
	Props,
	DefaultType extends React.ElementType,
	Render extends OverridenForwardRefRenderFunction<Props, DefaultType> = OverridenForwardRefRenderFunction<
		Props,
		DefaultType
	>
>(render: Render): OverridableComponent<Props, DefaultType> {
	return React.forwardRef(render);
}
